/**
 * @file Serialization and deserialization of (potentialy special) message types.
 */

/**
 * The classification of a message type.
 * - `normal`: A regular message type.
 * - `result`: A successful reply to the original message
 * - `error`: An error reply to the original message
 *
 * The classification is appended to the message type with a slash, e.g. a
 * successful reply to `someMessage` would be `someMessage/result`.
 */
type MessageTypeClassification = 'normal' | 'result' | 'error'

function serializeMessageType<TMessageType extends string>(
  messageType: TMessageType,
  classification?: undefined,
): TMessageType
function serializeMessageType<TMessageType extends string, TClassification extends MessageTypeClassification>(
  messageType: TMessageType,
  classification: TClassification,
): TClassification extends 'normal' ? TMessageType : `${TMessageType}/${TClassification}`

function serializeMessageType(messageType: string, classification?: MessageTypeClassification): string {
  return !!classification && classification !== 'normal' ? `${messageType}/${classification}` : messageType
}

function deserializeMessageType(messageType: string): {
  originalMessageType: string
  classification: MessageTypeClassification
} {
  const lastSlashIndex = messageType.lastIndexOf('/')
  if (lastSlashIndex === -1) {
    return { originalMessageType: messageType, classification: 'normal' }
  }
  const originalMessageType = messageType.slice(0, lastSlashIndex)
  const classification = messageType.slice(lastSlashIndex + 1) as MessageTypeClassification
  switch (classification) {
    case 'result':
    case 'error':
      return { originalMessageType, classification }
    default:
      return { originalMessageType: messageType, classification: 'normal' }
  }
}

export type { MessageTypeClassification }
export { serializeMessageType, deserializeMessageType }
